/**
 * Theme store (Zustand v5).
 *
 * Manages light/dark theme toggling via CSS class on <html>.
 * Persists preference to localStorage.
 *
 * Cross-References:
 *   - ADR-006: CSS Custom Properties
 *   - Style Schema §1: Design tokens (light/dark variants)
 */
import { create } from 'zustand';

type Theme = 'light' | 'dark';

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  const stored = localStorage.getItem('vortex-theme');
  if (stored === 'light' || stored === 'dark') return stored;
  // Default to dark — user preference; they can toggle to light via the header.
  return 'dark';
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(theme);
  localStorage.setItem('vortex-theme', theme);
}

export const useThemeStore = create<ThemeState>()((set) => {
  const initial = getInitialTheme();
  applyTheme(initial);

  return {
    theme: initial,

    setTheme: (theme) => {
      applyTheme(theme);
      set({ theme });
    },

    toggleTheme: () =>
      set((state) => {
        const next = state.theme === 'light' ? 'dark' : 'light';
        applyTheme(next);
        return { theme: next };
      }),
  };
});
